/**
 * Funnel analytics for the v3 landing pages.
 *
 * Transcribed from the `track()` reference implementation in the design
 * package's `.dc.html` sources, which the handoff names as the reference
 * ("search `track(` in Landing Page v3.dc.html").
 *
 * THE PRIVACY RULE IS THE LOAD-BEARING ONE. The handoff's acceptance check 5
 * is "no event contains an email address or free text", and its §5 says to
 * send `has_text` and `text_length` instead. So this module takes the event
 * properties it is given and strips anything that looks like a payload we
 * promised not to send, rather than trusting every call site to remember.
 * A call site that forgets is a privacy incident, not a bug.
 *
 * Attribution is read once and kept for the session: UTMs and LinkedIn's
 * `li_fat_id` ride on every event and on the lead. The handoff asks for a
 * 90-day first-party cookie in production; `sessionStorage` is what the
 * prototype uses and what ships here, because the cookie has to be written
 * only after the consent banner exists, and that banner is a separate piece
 * of work. Storing it per session keeps the funnel measurable without
 * planting a persistent identifier nobody has consented to yet.
 */

import { createScrub } from '../../../analytics/scrub';
import { pushToDataLayer } from '../../../analytics/gtm';
import { ensureCampaignTag } from '../../../analytics/campaignTag';
import { STICKY_BAR_BREAKPOINT } from './breakpoints';

const ATTR_KEY = 'aq_attr';
/*
 * The five campaign tags, plus the three click ids the ad platforms append
 * themselves. A click id is the only thing that lets a platform match a
 * conversion back to the click that paid for it, and it exists solely in the
 * URL of that one visit: miss it and it is gone for good.
 */
const UTM_FIELDS = [
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
  // The ad set id. LinkedIn Campaign Manager appends it to every ad as
  // `utm_id={{AD_SET_ID}}` whether or not anyone asked for it, and it is
  // finer grained than `utm_campaign`.
  'utm_id',
  'li_fat_id', 'fbclid', 'gclid',
];

/** Ordered funnel stages. `stage_reached` fires once per stage, forward only. */
export const STAGES = [
  'landing', 'feed_interacted', 'form_started', 'form_submitted', 'lead_created',
];

/**
 * This page's policy differs from the `/c/` funnel's in exactly two ways, both
 * deliberate and both guarded by a test:
 *
 *  - `publication` is banned here. The newsroom demo form collects it; `/c/`
 *    has no such field.
 *  - Primitive arrays survive, because `topics: ['Health']` and
 *    `fields_touched: ['email','topics']` are the shape of two of these events.
 *    The filter is narrow: `[{ email }]` is still dropped element by element.
 *
 * Everything else comes from the shared core, which is a strict upgrade on what
 * this file used to do: suffix matching so `userEmail` and `storyTitle` are
 * caught rather than only seven exact names, 64-character truncation, and
 * nested objects dropped instead of forwarded verbatim.
 */
const scrub = createScrub({
  extraBanned: ['publication'],
  // Both end in a banned suffix and both are the SAFE substitutes the design
  // package mandates in place of the free text: a boolean and a length. They
  // carry no content, which is the entire point of sending them.
  allowKeys: ['has_text', 'text_length'],
  allowPrimitiveArrays: true,
  keepNullish: true,
});

export function readAttribution() {
  const out = {};
  try {
    const q = new URLSearchParams(window.location.search);
    let saved = {};
    try {
      saved = JSON.parse(window.sessionStorage.getItem(ATTR_KEY) || '{}');
    } catch {
      /* Corrupt or unavailable storage is not worth failing the page over. */
    }
    UTM_FIELDS.forEach((k) => { out[k] = q.get(k) || saved[k] || ''; });
    out.referrer = saved.referrer ?? document.referrer;
    out.sid = saved.sid || Math.random().toString(36).slice(2);
    try {
      window.sessionStorage.setItem(ATTR_KEY, JSON.stringify(out));
    } catch {
      /* Private mode. Attribution still applies for this page view. */
    }
  } catch {
    /* No window, or a malformed query string. */
  }
  return out;
}

/**
 * One tracker per page view. Holds the funnel position, so `stage_reached`
 * and `step_abandoned` can answer "how far did this visitor get".
 */
export function createTracker({ campaign, page }) {
  // Synchronous, and first. `createTracker` is called from a `useMemo`, which
  // runs during render, so the Consent Mode defaults reach `dataLayer` before
  // any effect can push an event. See campaignTag.js for why this cannot live
  // in a component effect.
  ensureCampaignTag();

  const t0 = Date.now();
  const state = { stage: 'landing', touched: {}, scrollSeen: {}, maxScroll: 0, left: false };

  function track(event, props = {}) {
    const payload = {
      event,
      campaign,
      page,
      stage: state.stage,
      // Same threshold as the sticky bar, from the same constant, so the
      // analytics view of "mobile" cannot drift from the page's.
      viewport: (typeof window !== 'undefined' && window.innerWidth < STICKY_BAR_BREAKPOINT)
        ? 'mobile'
        : 'desktop',
      ts: new Date().toISOString(),
      ...readAttribution(),
      ...scrub(props),
    };
    // One push site, through the same helper the rest of the app uses.
    //
    // The `navigator.sendBeacon(window.ALCHEMIQ_EVENTS_URL)` branch that used
    // to sit here is gone. It had NO consent check: Consent Mode gates GTM
    // tags, not our own beacons, so it was the one path that would have
    // transmitted a visitor's data after they pressed Decline. It was also
    // dead, since nothing ever set that global.
    pushToDataLayer(payload);
    return payload;
  }

  return {
    track,
    get stage() { return state.stage; },
    /** Forward only: a visitor who scrolls back up has not un-converted. */
    advance(next) {
      if (STAGES.indexOf(next) > STAGES.indexOf(state.stage)) {
        state.stage = next;
        track('stage_reached', {
          reached: next,
          seconds: Math.round((Date.now() - t0) / 1000),
        });
      }
    },
    /** First touch of any field is `form_started`; later touches are recorded only. */
    touch(field) {
      if (state.touched[field]) return;
      const first = Object.keys(state.touched).length === 0;
      state.touched[field] = true;
      if (first) {
        this.advance('form_started');
        track('form_started', { first_field: field });
      }
    },
    scrolled(percent) {
      state.maxScroll = Math.max(state.maxScroll, percent);
      [25, 50, 75, 100].forEach((mark) => {
        if (percent >= mark - 1 && !state.scrollSeen[mark]) {
          state.scrollSeen[mark] = true;
          track('scroll_depth', { percent: mark });
        }
      });
    },
    /**
     * Fires once, on the way out, and only for a visitor who never converted.
     * `pagehide` and a hidden `visibilitychange` both route here because
     * neither alone is reliable across browsers.
     */
    abandoned() {
      if (state.left || state.stage === 'lead_created') return;
      state.left = true;
      track('step_abandoned', {
        last_stage: state.stage,
        seconds_on_page: Math.round((Date.now() - t0) / 1000),
        fields_touched: Object.keys(state.touched),
        max_scroll_percent: state.maxScroll,
      });
    },
  };
}
